import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Button,
  Card,
  CardContent,
  DataTable,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  FormField,
  FullscreenSpinner,
  Input,
  Spinner,
  useToast,
  type Column,
} from '@shared/ui';
import { taxonomyApi, type HsCode } from '@entities/taxonomy';

const schema = z.object({
  code: z.string().regex(/^\d{2,16}$/, '2–16 цифр'),
  description: z.string().min(1).max(512),
});

type Values = z.infer<typeof schema>;

const COLUMNS: Column<HsCode>[] = [
  { key: 'code', header: 'Code', cell: (r) => <span className="font-mono">{r.code}</span> },
  { key: 'desc', header: 'Описание', cell: (r) => r.description },
];

export function HsCodesPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['hs-codes', q],
    queryFn: () => taxonomyApi.searchHsCodes(q, 100),
  });

  const upsert = useMutation({
    mutationFn: (v: Values) => taxonomyApi.upsertHsCode(v),
    onSuccess: () => {
      toast.success('Сохранено');
      setOpen(false);
      void qc.invalidateQueries({ queryKey: ['hs-codes'] });
    },
    onError: (e: Error) => toast.error('Не удалось сохранить', e.message),
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { code: '', description: '' },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold tracking-tight">HS-коды (ТН ВЭД)</h1>
        <Button onClick={() => setOpen(true)}>+ Добавить</Button>
      </div>

      <Input
        placeholder="Поиск по коду или описанию"
        defaultValue={q}
        onBlur={(e) => setQ(e.target.value)}
        className="max-w-md"
      />

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <FullscreenSpinner />
          ) : (
            <DataTable
              rows={data ?? []}
              columns={COLUMNS}
              rowKey={(r) => r.code}
              empty={<EmptyState title="Кодов не найдено" />}
            />
          )}
        </CardContent>
      </Card>

      <Dialog
        open={open}
        onOpenChange={(o) => {
          if (!o) reset();
          setOpen(o);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upsert HS-code</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit((v) => upsert.mutate(v))} className="space-y-3">
            <FormField label="Code" htmlFor="code" error={errors.code?.message}>
              <Input id="code" {...register('code')} placeholder="8516" />
            </FormField>
            <FormField label="Описание" htmlFor="description" error={errors.description?.message}>
              <Input id="description" {...register('description')} />
            </FormField>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                Отмена
              </Button>
              <Button type="submit" disabled={upsert.isPending}>
                {upsert.isPending ? <Spinner /> : 'Сохранить'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
